<template>
    <transition name="fade">
        <a class="design" v-show="ready" v-on:click="onClick">
            <img
                class="design-image"
                v-bind:src="imageUrl"
                v-if="imageUrl"
                ref="image"
                v-on:load="onLoad"
            />
            <p class="design-name">{{ locale(name) }}</p>
        </a>
    </transition>
</template>

<style scoped>
.design {
    color: #000000;
    cursor: pointer;
    display: inline-block;
    margin-right: 20px;
    padding-bottom: 50px;
    position: relative;
    text-decoration: none;
    vertical-align: bottom;
    white-space: normal;
    width: 160px;
}

.design:last-child {
    margin-right: 0px;
}

.design .design-image {
    height: auto;
    width: 160px;
}

.design .design-name {
    bottom: 0px;
    font-size: 12px;
    font-weight: 500;
    height: 30px;
    left: 0px;
    margin: 0px auto 0px auto;
    position: absolute;
    right: 0px;
}

@media screen and (max-height: 1340px) {

    .design .design-name {
        font-size: 10px;
    }
}
</style>

<script>
export const Design = {
    name: "design",
    props: {
        identifier: {
            type: String,
            required: true
        },
        size: {
            type: Number,
            default: 320
        }
    },
    data: function() {
        return {
            product: null,
            loaded: false
        };
    },
    computed: {
        ready() {
            return this.product && this.loaded;
        },
        name() {
            return this.product ? this.product.description || this.modelNormalized : "";
        },
        modelNormalized() {
            if (!this.product) return null;
            if (!this.product.model) return null;
            return this.product.model
                .split("_")
                .map(v => v[0].toUpperCase() + v.slice(1))
                .join(" ");
        },
        imageUrl() {
            // in case the product string starts with a query prefix then
            // it should be included as a part of the query string
            if (this.isQuery && this.product) {
                return `${this.ripeUrl}compose?${this.buildQuery()}&size=${this.size}&crop=1`;
            }
            // if the string starts with the dku prefix then it should
            // be read as a proper DKU based string
            else if (this.isDku && this.product) {
                return `${this.ripeUrl}compose?${this.buildQuery()}&size=${this.size}&crop=1`;
            }
            // otherwise it's "just" an "normal" product ID that should
            // included directly on the URL
            else if (this.isProductId && this.product) {
                return `${this.ripeUrl}compose?product_id=${this.identifier}&size=${this.size}&crop=1`;
            }
            return null;
        },
        design(element, deep = true) {
            return this.buildDesign(deep);
        },
        isQuery() {
            return this.identifier.startsWith("query:");
        },
        isDku() {
            return this.identifier.startsWith("dku:");
        },
        isProductId() {
            return !this.isQuery && !this.isDku;
        }
    },
    created: async function() {
        if (this.isQuery) {
            this.product = this.$ripe._queryToSpec(this.identifier.slice(6));
        } else if (this.isDku) {
            this.product = await this.$ripe.configDkuP(this.identifier.slice(4));
        } else if (this.isProductId) {
            this.product = await this.$ripe.configResolveP(this.identifier);
        }
    },
    methods: {
        onLoad() {
            this.loaded = true;
        },
        onClick() {
            this.$emit("selected", this.buildDesign());
        },
        buildQuery(names = ["brand", "model", "variant", "parts"]) {
            if (!this.product) return "";
            const spec = {};
            for (const name of ["brand", "model", "variant", "parts"]) {
                spec[name] = this.product[name];
            }
            return this.$ripe._specToQuery(spec);
        },
        buildDesign(deep = true) {
            // creates the design object as a copy of the associated
            // product instance, the copy strategy is defined by the
            // deep flag (notice that a deep copy is considered expensive)
            const design = deep
                ? JSON.parse(JSON.stringify(this.product))
                : Object.assign({}, this.product);

            // moves the complete set of contents from the extras map
            // into the base object and then removes the map
            if (design.extras_m) {
                Object.assign(design, design.extras_m);
                delete design.extras_m;
            }

            // in case a DKU is defined in the design deletes the value as
            // it would create issues with the spec definition
            if (design.dku) delete design.dku;

            // ensures the setting of the variant field, making sure that any
            // kind of update in the RIPE instance is complete
            design.variant = design.variant ? design.variant : null;

            // updates the design initials, engraving and initials extra
            // values so that they exist in the options even if they are
            // not currently set on the design
            design.initials = design.initials ? design.initials : "";
            design.engraving = design.engraving ? design.engraving : null;
            design.initialsExtra = design.initialsExtra ? design.initialsExtra : {};

            // returns the final design object as the calculated attribute
            // to be used by the caller
            return design;
        }
    }
};

export default Design;
</script>
